// src/components/pages/CancelBooking.tsx

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

import { toast } from 'sonner';
import {
  Loader2,
  Bell,
  CalendarDays,
  Clock,
  UserRound,
  Phone,
  MapPin,
} from 'lucide-react';

import { cancelarCita, obtenerCita } from '@/components/hooks/useCitas';
import { useEmpresa } from '@/components/hooks/useEmpresa';

const CancelBooking = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const idCita = searchParams.get('id');

  const [motivo, setMotivo] = useState('');
  const [cita, setCita] = useState<any>(null);
  const [empresaId, setEmpresaId] = useState<string | null>(null);

  const [loadingCita, setLoadingCita] = useState(true);
  const [loadingCancel, setLoadingCancel] = useState(false);
  const [logoError, setLogoError] = useState(false);

  // Hook oficial para obtener empresa
  const { empresa, loadingEmpresa } = useEmpresa(empresaId || undefined);

  /* ============================================================
     LOAD CITA + SET EMPRESA ID
  ============================================================ */
  useEffect(() => {
    if (idCita) fetchCita();
    else setLoadingCita(false);
  }, [idCita]);

  const fetchCita = async () => {
    setLoadingCita(true);

    const resp = await obtenerCita(idCita!);

    if (!resp.ok || !resp.cita) {
      toast.error('Cita no encontrada');
      setLoadingCita(false);
      return;
    }

    setCita(resp.cita);
    setEmpresaId(resp.cita.id_empresa);

    setLoadingCita(false);
  };

  /* ============================================================
     CANCELAR CITA
  ============================================================ */
  const onCancelar = async () => {
    if (!cita || cita.estado_cita !== 'confirmado') {
      toast.error('Solo se pueden cancelar citas en estado confirmado');
      return;
    }

    setLoadingCancel(true);

    const resp = await cancelarCita(idCita!, motivo.trim());

    if (resp.ok) {
      toast.success('Cita cancelada correctamente');
      await fetchCita();
    }

    setLoadingCancel(false);
  };

  const logoUrl =
    empresa?.logo && typeof empresa.logo === 'string'
      ? `${empresa.logo}?t=${Date.now()}`
      : null;

  /* ============================================================
     RENDER
  ============================================================ */

  const isLoading = loadingCita || loadingEmpresa;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-black px-4">
      <div className="w-full max-w-md bg-white dark:bg-[#1c1c1e] shadow-lg overflow-hidden">
        {/* HEADER */}
        {empresa && (
          <div className="bg-[#103254] dark:bg-[#0a1a16] text-white">
            <div className="flex items-center justify-center gap-3 py-6 px-4">
              {logoUrl && !logoError && (
                <img
                  src={logoUrl}
                  alt="logo"
                  className="w-16 h-16 object-contain border bg-white p-1 rounded-full"
                  onError={() => setLogoError(true)}
                />
              )}
              <h1 className="text-lg font-semibold text-white text-center">
                {empresa.empresa}
              </h1>
            </div>
          </div>
        )}

        {/* CONTENIDO */}
        <div className="px-4 py-6 space-y-6">
          {isLoading ? (
            <div className="flex justify-center items-center h-32">
              <Loader2 className="animate-spin h-8 w-8 text-[#103254] dark:text-white" />
            </div>
          ) : !idCita ? (
            <p className="text-red-600 dark:text-red-400 text-center">
              ID de cita no proporcionado.
            </p>
          ) : !cita ? (
            <p className="text-red-600 dark:text-red-400 text-center">Cita no encontrada.</p>
          ) : cita.estado_cita === 'cancelado' ? (
            <>
              <div className="bg-red-100 dark:bg-red-500/10 text-red-800 dark:text-red-400 text-sm p-3 border border-red-300 dark:border-red-500/30 text-center">
                Esta cita ya fue cancelada previamente.
              </div>

              {empresa?.reservas_online_citas && (
                <Button
                  variant="outline"
                  onClick={() =>
                    window.location.assign(`/client-booking/${cita.id_empresa}`)
                  }
                  className="w-full dark:border-white/20 dark:text-white dark:hover:bg-white/10"
                >
                  Reagendar cita en {empresa.empresa}
                </Button>
              )}
            </>
          ) : cita.estado_cita === 'completado' ? (
            <>
              <div className="bg-green-100 dark:bg-green-500/10 text-green-800 dark:text-green-400 text-sm p-3 border border-green-300 dark:border-green-500/30 text-center">
                Esta cita ya fue completada y no puede cancelarse.
              </div>

              {empresa?.reservas_online_citas && (
                <Button
                  variant="outline"
                  onClick={() =>
                    window.location.assign(`/client-booking/${cita.id_empresa}`)
                  }
                  className="w-full dark:border-white/20 dark:text-white dark:hover:bg-white/10"
                >
                  Reagendar cita en {empresa.empresa}
                </Button>
              )}
            </>
          ) : cita.estado_cita !== 'confirmado' ? (
            <>
              <div className="bg-yellow-100 dark:bg-yellow-500/10 text-yellow-800 dark:text-yellow-400 text-sm p-3 border border-yellow-300 dark:border-yellow-500/30 text-center">
                Solo se pueden cancelar citas en estado confirmado.
              </div>

              {empresa?.reservas_online_citas && (
                <Button
                  variant="outline"
                  onClick={() =>
                    window.location.assign(`/client-booking/${cita.id_empresa}`)
                  }
                  className="w-full dark:border-white/20 dark:text-white dark:hover:bg-white/10"
                >
                  Reagendar cita en {empresa.empresa}
                </Button>
              )}
            </>
          ) : (
            <>
              <div className="bg-gray-50 dark:bg-white/5 border dark:border-white/10 p-4 shadow-sm space-y-2 text-sm">
                <p className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                  <Bell className="w-4 h-4" />
                  <strong>Servicio:</strong> {cita.nombre_servicio}
                </p>
                <p className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                  <UserRound className="w-4 h-4" />
                  <strong>Profesional:</strong> {cita.trabajadores}
                </p>
                <p className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                  <CalendarDays className="w-4 h-4" />
                  <strong>Fecha:</strong> {cita.fecha_cita}
                </p>
                <p className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                  <Clock className="w-4 h-4" />
                  <strong>Hora:</strong> {cita.hora_cita}
                </p>
              </div>

              {/* MOTIVO */}
              <div>
                <Label
                  htmlFor="motivo"
                  className="text-[#103254] dark:text-white font-semibold text-xs uppercase"
                >
                  Motivo de cancelación
                  <span className="text-gray-400 dark:text-gray-500 text-sm"> (opcional)</span>
                </Label>

                <Input
                  id="motivo"
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Ej. No podré asistir…"
                  className="mt-1 dark:bg-black/40 dark:border-white/10 dark:text-white"
                />
              </div>

              <Button
                onClick={onCancelar}
                disabled={loadingCancel}
                className="w-full bg-red-600 text-white hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 flex items-center justify-center py-2"
              >
                {loadingCancel && (
                  <Loader2 className="animate-spin mr-2 h-5 w-5" />
                )}
                {loadingCancel ? 'Cancelando…' : 'Cancelar cita'}
              </Button>
            </>
          )}

          {/* VOLVER AL LOGIN */}
          <Button
            type="button"
            variant="outline"
            className="w-full dark:border-white/20 dark:text-white dark:hover:bg-white/10"
            onClick={() => navigate('/')}
          >
            Volver al inicio
          </Button>
        </div>

        {/* FOOTER */}
        {empresa && (empresa.telefono || empresa.direccion) && (
          <footer className="py-4 border-t border-gray-200 dark:border-white/10">
            <div className="flex flex-wrap justify-center items-center gap-3 text-xs px-3 text-[#103254] dark:text-white/80">
              {empresa.telefono && (
                <span className="flex items-center gap-1">
                  <Phone className="w-3.5 h-3.5" />
                  <a href={`tel:${empresa.telefono}`} className="underline">
                    {empresa.telefono}
                  </a>
                </span>
              )}
              {empresa.direccion && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" />
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      empresa.direccion
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    {empresa.direccion}
                  </a>
                </span>
              )}
            </div>
          </footer>
        )}
      </div>
    </div>
  );
};

export default CancelBooking;